import React, { useState, useImperativeHandle, forwardRef } from "react";
import { useSelector } from "react-redux";
import URL from "../Redux/Url";

const ShippingComponent = forwardRef(({
  cart,
  userInfo,
  selectedRate,
  setSelectedRate,
  qualifiesForFreeShipping,
}, ref) => {
  const [rates, setRates]         = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const guest = useSelector((state) => state.guest);

  const recipientEmail = userInfo?.email || guest?.email || "";
  const recipientName  = userInfo?.name  || "Guest";

  const fetchRates = async () => {
    if (!cart?.cartItems?.length) return;

    const firstItem = cart.cartItems[0];
    const dims = firstItem.dimensions;

    if (!dims) {
      alert("Product dimensions not found. Please remove this item and add it again.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        dimensions: {
          length:       Number(dims.length),
          width:        Number(dims.width),
          height:       Number(dims.height),
          distanceUnit: dims.distance_unit,
          weight:       Number(dims.weight),
          massUnit:     dims.mass_unit,
        },
        toAddress: {
          name:    recipientName,
          street1: cart.shippingAddress.street1,
          city:    cart.shippingAddress.city,
          state:   cart.shippingAddress.state,
          zip:     cart.shippingAddress.zipcode,
          country: cart.shippingAddress.country || "US",
          email:   recipientEmail,
          phone:   "9095555555",
        },
      };

      const res = await fetch(`${URL}/api/shipment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Error calculating shipping");
      }

      if (data.rates) {
        setRates(data.rates);

        if (qualifiesForFreeShipping) {
          const groundAdvantage = data.rates.find(
            (r) => r.servicelevel?.token === "usps_ground_advantage"
          );
          const autoSelect = groundAdvantage || data.rates[0];
          if (autoSelect) {
            setSelectedRate({ ...autoSelect, amount: "0.00" });
          }
        }
      }
    } catch (error) {
      console.error("Shipping Error:", error.message);
      if (!qualifiesForFreeShipping) {
        alert(error.message || "Error calculating shipping");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ fetchRates }));

  if (qualifiesForFreeShipping) {
    return (
      <div className="mt-3 w-100">
        {isLoading ? (
          <div className="text-center text-muted small py-2">
            Selecting shipping method...
          </div>
        ) : selectedRate ? (
          <div className="alert alert-success py-2 text-center small mb-0">
            <i className="fas fa-truck me-2"></i>
            <strong>USPS Ground Advantage</strong> — Free (
            {selectedRate.estimated_days} days)
          </div>
        ) : (
          <div className="text-center text-muted small py-2">
            Loading shipping...
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 w-100">
      <button
        type="button"
        className="btn btn-primary w-100"
        onClick={fetchRates}
        disabled={isLoading}
      >
        {isLoading ? "Calculating..." : "Get Shipping Rates"}
      </button>

      {rates.length > 0 && (
        <select
          className="form-select mt-2"
          value={selectedRate?.object_id || ""}
          onChange={(e) => {
            const r = rates.find((rate) => rate.object_id === e.target.value);
            setSelectedRate(r);
          }}
        >
          <option value="">Select Shipping Method</option>
          {rates.map((rate) => (
            <option key={rate.object_id} value={rate.object_id}>
              {rate.provider} - {rate.servicelevel?.name}: ${rate.amount}
            </option>
          ))}
        </select>
      )}
    </div>
  );
});

ShippingComponent.displayName = "ShippingComponent";

export default ShippingComponent;